import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { QuoteSummary } from '@shared/types/index.ts';
import {
  isScheduledQuote,
  isPartlyScheduledQuote,
  LOCKED_QUOTE_STATUSES,
  type Permission,
} from '@shared/constants/index.ts';
import { usePlanFeature } from '../plan-gate.tsx';
import { useSession } from '../../state/session.tsx';
import { quoteApi } from '../../api/endpoints.ts';
import { PdfDownloadButton } from '../pdf-download.tsx';
import { ScheduleQuoteButton } from './schedule-button.tsx';
import { SendQuoteButton } from './send-button.tsx';
import { CloneQuoteButton } from './clone-button.tsx';
import { QuoteActionsOverflow } from './actions-overflow.tsx';
import { Button } from '../ui.tsx';

/** Choose from actual status and permission; sending/booking still re-read
 * and use their existing guarded, versioned flows when invoked. */
export function quoteListNextAction(
  quote: QuoteSummary,
  can: (permission: Permission) => boolean,
  hasScheduling = true,
) {
  const scheduled = isScheduledQuote(quote);
  if (hasScheduling && scheduled && can('schedule:read')) return 'schedule';
  if (hasScheduling && !scheduled && LOCKED_QUOTE_STATUSES.includes(quote.status) && can('schedule:write'))
    return 'schedule';
  if (!quote.isExpired && can('quotes:write')) {
    if (quote.status === 'draft') return 'continue';
    if (quote.status === 'ready') return 'send';
  }
  return 'open';
}

export function QuoteListActions({
  quote,
  className = '',
  onDelete,
}: {
  quote: QuoteSummary;
  className?: string;
  onDelete: (quote: QuoteSummary) => void;
}) {
  const { t } = useTranslation();
  const { can } = useSession();
  const cache = useQueryClient();
  const hasScheduling = usePlanFeature('scheduling');
  const action = quoteListNextAction(quote, can, hasScheduling);
  return (
    <div className={`table-actions flex flex-wrap items-center justify-end gap-2 ${className}`}>
      {action === 'schedule' ? (
        <ScheduleQuoteButton quote={quote} showExistingScheduleLink={false} />
      ) : action === 'send' ? (
        <SendQuoteButton quote={quote} />
      ) : (
        <Link to={`/quotes/${quote.id}`} className="btn btn-primary">
          {t(action === 'continue' ? 'quotes.list.continueQuote' : 'quotes.list.openQuote')}
        </Link>
      )}
      <QuoteActionsOverflow label={t('quotes.list.moreActions')}>
        <PdfDownloadButton
          url={quoteApi.pdfUrl(quote.id)}
          variant="link"
          recovery={
            can('pdf:generate')
              ? {
                  regenerate: () =>
                    quoteApi.generatePdf(quote.id).then(() => {
                      void cache.invalidateQueries({ queryKey: ['quotes'] });
                      void cache.invalidateQueries({ queryKey: ['quote', quote.id] });
                    }),
                  retryUrl: quoteApi.pdfUrl(quote.id),
                }
              : undefined
          }
        >
          {t('quotes.list.downloadPdf')}
        </PdfDownloadButton>
        {action === 'schedule' && isPartlyScheduledQuote(quote) && quote.scheduledSeriesId && can('schedule:read') ? (
          <Link to={`/schedule/series/${quote.scheduledSeriesId}`} className="btn btn-ghost">
            {t('quotes.schedule.viewSchedule')}
          </Link>
        ) : null}
        {action !== 'send' ? <SendQuoteButton quote={quote} /> : null}
        {can('quotes:write') ? <CloneQuoteButton quote={quote} /> : null}
        {action !== 'schedule' ? <ScheduleQuoteButton quote={quote} /> : null}
        {can('quotes:delete') ? (
          <div className="mt-1 border-t border-[color:var(--color-line)] pt-3">
            <Button variant="danger" className="w-full" onClick={() => onDelete(quote)}>
              {t('quotes.delete.action')}
            </Button>
          </div>
        ) : null}
      </QuoteActionsOverflow>
    </div>
  );
}
